import {useCallback, useRef, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {useCreativeImageLoader} from './useCreativeImageLoader'
import {useImageDropTarget} from './useImageDropTarget'

interface CreativeImageSlotProps {
  /** Analytics slot name: 'background' | 'key_art' | 'screenshot' | 'icon' */
  slot: string
  label: string
  image: HTMLImageElement | null
  onImageLoaded: (image: HTMLImageElement) => void
  onClear: () => void
}

/** One uploadable image (thumbnail + pick / replace / remove); also a drop target. */
const CreativeImageSlot = ({slot, label, image, onImageLoaded, onClear}: CreativeImageSlotProps) => {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const {busy, error, loadFile} = useCreativeImageLoader(slot, onImageLoaded)
  const handleDropFile = useCallback((file: File) => void loadFile(file, 'drop'), [loadFile])
  const {dragOver, dropProps} = useImageDropTarget(handleDropFile)

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (file) void loadFile(file, 'pick')
    },
    [loadFile],
  )

  const handlePick = useCallback(() => inputRef.current?.click(), [])

  return (
    <div className={`caa-slot${dragOver ? ' is-dragover' : ''}`} {...dropProps}>
      <span className="caa-label">{label}</span>
      {image && <img className="caa-slot-thumb" src={image.src} alt={label} />}
      <div className="caa-slot-actions">
        <button type="button" className="cvt-btn cvt-btn-secondary" onClick={handlePick} disabled={busy}>
          {busy
            ? t('creativeAssets.upload.busy')
            : image
              ? t('creativeAssets.upload.replace')
              : t('creativeAssets.upload.pick')}
        </button>
        {image && (
          <button type="button" className="cvt-btn cvt-btn-ghost" onClick={onClear} disabled={busy}>
            {t('creativeAssets.upload.clear')}
          </button>
        )}
      </div>
      <p className="cvt-hint">{t('creativeAssets.upload.dropHint')}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={styles.hiddenInput}
      />
      {error && <p className="cvt-error">{error}</p>}
    </div>
  )
}

const styles = {
  hiddenInput: {display: 'none'},
}

export default CreativeImageSlot
